type SupportedErrorCode = 404 | 500 | 501 | 503;

type ErrorCopy = {
    eyebrow: string;
    title: string;
    description: string;
};

const COPIES: Record<SupportedErrorCode, ErrorCopy> = {
    404: {
        eyebrow: 'PAGE NOT FOUND',
        title: '요청한 정보를 찾을 수 없습니다',
        description: '주소가 변경되었거나 삭제된 정보일 수 있습니다.',
    },
    500: {
        eyebrow: 'INTERNAL SERVER ERROR',
        title: '요청을 처리하는 중 문제가 발생했습니다',
        description: '현재 화면의 작성 내용은 유지됩니다. 잠시 후 다시 시도해 주세요.',
    },
    501: {
        eyebrow: 'NOT IMPLEMENTED',
        title: '아직 제공하지 않는 기능입니다',
        description: '요청하신 기능은 현재 준비 중입니다.',
    },
    503: {
        eyebrow: 'SERVICE UNAVAILABLE',
        title: '서비스 연결을 다시 준비하고 있습니다',
        description: '서버 연결이 잠시 끊겼습니다. 현재 화면의 작업 내용은 유지되며, 잠시 후 다시 시도할 수 있습니다.',
    },
};

const STYLE_ID = 'wiz-system-error-style';
const OVERLAY_ID = 'wiz-system-error-overlay';
const MAX_CONSOLE_RESPONSE_LENGTH = 10000;
let lastSignature = '';
let lastShownAt = 0;

function isSameOrigin(url: any) {
    try {
        return new URL(`${url || ''}`, location.href).origin === location.origin;
    } catch (_error) {
        return false;
    }
}

function isApiRequest(url: any) {
    if (!isSameOrigin(url)) return false;
    const path = new URL(`${url || ''}`, location.href).pathname;
    return path.startsWith('/wiz/api/') || path.startsWith('/api/');
}

function shouldHandleRequest(url: any) {
    if (!isApiRequest(url)) return false;
    return Number((window as any).__wizInlineHttpErrorHandling || 0) <= 0;
}

function consoleResponseBody(body: any) {
    if (typeof body !== 'string') return body;
    const trimmed = body.trim();
    if (!trimmed) return '';
    const limited = trimmed.length > MAX_CONSOLE_RESPONSE_LENGTH
        ? `${trimmed.slice(0, MAX_CONSOLE_RESPONSE_LENGTH)}\n… (응답 본문 생략)`
        : trimmed;
    try {
        return JSON.parse(limited);
    } catch (_error) {
        return limited;
    }
}

function logHttpError(
    transport: 'fetch' | 'xhr',
    method: any,
    url: any,
    status: any,
    statusText: any,
    responseBody: any,
) {
    console.error(`[WIZ] HTTP ${Number(status || 0)} ${`${method || 'GET'}`.toUpperCase()} ${url || ''}`, {
        transport,
        method: `${method || 'GET'}`.toUpperCase(),
        url: `${url || ''}`,
        status: Number(status || 0),
        statusText: `${statusText || ''}`,
        response: consoleResponseBody(responseBody),
    });
}

async function logFetchHttpError(response: Response, method: any, url: any) {
    let responseBody: any = '';
    try {
        responseBody = await response.clone().text();
    } catch (error) {
        responseBody = { message: '응답 본문을 읽을 수 없습니다.', error };
    }
    logHttpError('fetch', method, url, response.status, response.statusText, responseBody);
}

function xhrResponseBody(request: any) {
    try {
        if (!request.responseType || request.responseType === 'text') return request.responseText;
        return request.response;
    } catch (error) {
        return { message: '응답 본문을 읽을 수 없습니다.', error };
    }
}

function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
        #${OVERLAY_ID}{position:fixed;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(10,28,25,.52);backdrop-filter:blur(8px);font-family:"SUIT","Pretendard",sans-serif}
        #${OVERLAY_ID}[hidden]{display:none}
        #${OVERLAY_ID} .wiz-error-card{position:relative;width:min(560px,100%);overflow:hidden;border:1px solid rgba(255,255,255,.72);border-radius:24px;background:#fff;box-shadow:0 32px 90px rgba(6,27,23,.28)}
        #${OVERLAY_ID} .wiz-error-top{height:6px;background:linear-gradient(90deg,#244d47,#5c887f 62%,#a5632f)}
        #${OVERLAY_ID} .wiz-error-body{padding:30px 32px 26px}
        #${OVERLAY_ID} .wiz-error-head{display:flex;align-items:flex-start;gap:17px}
        #${OVERLAY_ID} .wiz-error-icon{display:flex;width:52px;height:52px;flex:0 0 auto;align-items:center;justify-content:center;border-radius:17px;background:#edf4f2;color:#a5632f;font-size:20px;font-weight:900}
        #${OVERLAY_ID} .wiz-error-copy{min-width:0;flex:1}
        #${OVERLAY_ID} .wiz-error-eyebrow{margin:1px 0 7px;color:#78908a;font-size:9px;font-weight:900;letter-spacing:.13em}
        #${OVERLAY_ID} h2{margin:0;color:#142d28;font-size:21px;font-weight:900;letter-spacing:-.035em;line-height:1.35;word-break:keep-all}
        #${OVERLAY_ID} .wiz-error-description{margin:10px 0 0;color:#687a76;font-size:13px;font-weight:600;line-height:1.7;word-break:keep-all}
        #${OVERLAY_ID} .wiz-error-meta{display:flex;gap:8px;margin-top:20px;border-radius:12px;background:#f6f8f7;padding:11px 13px;color:#7d8b88;font-size:10px;font-weight:700}
        #${OVERLAY_ID} .wiz-error-meta strong{color:#435b55}
        #${OVERLAY_ID} .wiz-error-actions{display:flex;flex-wrap:wrap;gap:9px;margin-top:24px}
        #${OVERLAY_ID} button{height:42px;border-radius:11px;padding:0 16px;font:800 12px "SUIT","Pretendard",sans-serif;cursor:pointer}
        #${OVERLAY_ID} .wiz-error-retry{border:0;background:#244d47;color:#fff}
        #${OVERLAY_ID} .wiz-error-dismiss{border:1px solid #d8e0de;background:#fff;color:#526763}
        #${OVERLAY_ID} .wiz-error-home{border:0;background:transparent;color:#71817e}
        @media(max-width:520px){#${OVERLAY_ID}{padding:14px}#${OVERLAY_ID} .wiz-error-body{padding:25px 21px 21px}#${OVERLAY_ID} .wiz-error-head{gap:13px}#${OVERLAY_ID} .wiz-error-icon{width:44px;height:44px;border-radius:14px}#${OVERLAY_ID} h2{font-size:18px}#${OVERLAY_ID} .wiz-error-actions{display:grid;grid-template-columns:1fr 1fr}#${OVERLAY_ID} button{width:100%}#${OVERLAY_ID} .wiz-error-home{grid-column:1/-1}}
    `;
    document.head.appendChild(style);
}

function errorTimestamp() {
    return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).format(new Date());
}

export function showSystemError(code: SupportedErrorCode = 500, source: string = '') {
    if (location.pathname.startsWith('/error/')) return;
    const signature = `${code}:${source}`;
    const now = Date.now();
    if (signature === lastSignature && now - lastShownAt < 1500) return;
    lastSignature = signature;
    lastShownAt = now;
    ensureStyles();

    let overlay = document.getElementById(OVERLAY_ID) as HTMLElement | null;
    if (!overlay) {
        overlay = document.createElement('section');
        overlay.id = OVERLAY_ID;
        overlay.setAttribute('role', 'alertdialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.innerHTML = `
            <div class="wiz-error-card">
                <div class="wiz-error-top"></div>
                <div class="wiz-error-body">
                    <div class="wiz-error-head">
                        <div class="wiz-error-icon" data-error-code></div>
                        <div class="wiz-error-copy">
                            <p class="wiz-error-eyebrow" data-error-eyebrow></p>
                            <h2 data-error-title></h2>
                            <p class="wiz-error-description" data-error-description></p>
                        </div>
                    </div>
                    <div class="wiz-error-meta"><span>오류 코드 <strong data-error-meta-code></strong></span><span>·</span><span data-error-time></span></div>
                    <div class="wiz-error-actions">
                        <button type="button" class="wiz-error-retry">다시 시도</button>
                        <button type="button" class="wiz-error-dismiss">현재 화면 유지</button>
                        <button type="button" class="wiz-error-home">자료함 홈</button>
                    </div>
                </div>
            </div>`;
        overlay.querySelector('.wiz-error-retry')?.addEventListener('click', () => location.reload());
        overlay.querySelector('.wiz-error-dismiss')?.addEventListener('click', () => { if (overlay) overlay.hidden = true; });
        overlay.querySelector('.wiz-error-home')?.addEventListener('click', () => { location.href = '/home'; });
        document.body.appendChild(overlay);
    }

    const copy = COPIES[code] || COPIES[500];
    const set = (selector: string, value: string) => {
        const element = overlay?.querySelector(selector);
        if (element) element.textContent = value;
    };
    set('[data-error-code]', `${code}`);
    set('[data-error-meta-code]', `${code}`);
    set('[data-error-eyebrow]', copy.eyebrow);
    set('[data-error-title]', copy.title);
    set('[data-error-description]', copy.description);
    set('[data-error-time]', errorTimestamp());
    overlay.hidden = false;
    (overlay.querySelector('.wiz-error-retry') as HTMLElement | null)?.focus();
}

export function installGlobalErrorHandling() {
    const xhrPrototype: any = XMLHttpRequest.prototype;
    const originalOpen = xhrPrototype.open;
    xhrPrototype.open = function (method: string, url: string, ...rest: any[]) {
        this.__wizRequestUrl = url;
        this.__wizRequestMethod = method;
        if (!this.__wizErrorBound) {
            this.__wizErrorBound = true;
            this.addEventListener('load', () => {
                const status = Number(this.status || 0) as SupportedErrorCode;
                if (status >= 400 && isApiRequest(this.__wizRequestUrl)) {
                    logHttpError(
                        'xhr',
                        this.__wizRequestMethod,
                        this.__wizRequestUrl,
                        status,
                        this.statusText,
                        xhrResponseBody(this),
                    );
                }
                if ([500, 501, 503].includes(status) && shouldHandleRequest(this.__wizRequestUrl)) {
                    showSystemError(status, this.__wizRequestUrl);
                }
            });
            this.addEventListener('error', (event: Event) => {
                if (isApiRequest(this.__wizRequestUrl)) {
                    console.error('[WIZ] XHR 네트워크 요청 실패', event, {
                        method: `${this.__wizRequestMethod || 'GET'}`.toUpperCase(),
                        url: `${this.__wizRequestUrl || ''}`,
                        status: Number(this.status || 0),
                        statusText: `${this.statusText || ''}`,
                    });
                }
                if (shouldHandleRequest(this.__wizRequestUrl)) showSystemError(503, this.__wizRequestUrl);
            });
        }
        return originalOpen.call(this, method, url, ...rest);
    };

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args: any[]) => {
        const input = args[0];
        const url = typeof input === 'string' || input instanceof URL ? `${input}` : input?.url;
        const method = `${args[1]?.method || args[0]?.method || 'GET'}`.toUpperCase();
        try {
            const response = await originalFetch(...args);
            const status = Number(response.status || 0) as SupportedErrorCode;
            if (status >= 400 && isApiRequest(url)) void logFetchHttpError(response, method, url);
            if ([500, 501, 503].includes(status) && shouldHandleRequest(url)) showSystemError(status, url);
            return response;
        } catch (error) {
            const isAbortError = `${(error as any)?.name || ''}` === 'AbortError';
            if (!isAbortError && isApiRequest(url)) {
                console.error('[WIZ] Fetch 네트워크 요청 실패', error, { method, url: `${url || ''}` });
            }
            if (
                !isAbortError
                && shouldHandleRequest(url)
            ) showSystemError(503, url);
            throw error;
        }
    };

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason = event.reason;
        const message = `${reason && (reason.message || reason) || ''}`;
        if (
            (
                `${reason && reason.name || ''}` === 'AbortError'
                || /AbortError/i.test(`${reason || ''}`)
            )
            && /Transition was skipped/i.test(message)
        ) {
            // Angular view transitions reject with this benign AbortError when
            // a guarded transition is cancelled in favor of an explicit exit.
            event.preventDefault();
            return;
        }
        if (/ResizeObserver|AbortError|cancell?ed/i.test(message)) return;
        console.error('[WIZ] 처리되지 않은 Promise 오류', reason);
        showSystemError(/network|fetch|connection/i.test(message) ? 503 : 500, 'unhandledrejection');
    });

    window.addEventListener('error', (event: ErrorEvent) => {
        const message = `${event.message || ''}`;
        if (!message || /ResizeObserver|Script error/i.test(message)) return;
        console.error('[WIZ] 처리되지 않은 JavaScript 오류', event.error || message, {
            filename: event.filename || '',
            line: event.lineno || 0,
            column: event.colno || 0,
        });
        showSystemError(500, event.filename || 'window');
    });
}
